import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { EDITOR_WEB_BUILD, servedAssetId } from "@/config/buildInfo";
import type { RuntimeConfig } from "@/config/schema";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

export type DeploymentIdentity = components["schemas"]["DeploymentIdentity"];

/**
 * What the BROWSER can state about itself, from the bundle it is executing.
 *
 * Not fetched, and it cannot be: the Editor Service does not serve the bundle
 * and must never restate what it cannot observe. `sourceCommit` is substituted
 * into this asset at build time and `assetId` is read off the URL the browser
 * actually loaded, so both describe the running code rather than a deployment's
 * intention about it.
 */
export interface WebBuildIdentity {
  sourceCommit: string;
  builtAt: string;
  assetId: string | null;
}

export function webBuildIdentity(): WebBuildIdentity {
  return {
    sourceCommit: EDITOR_WEB_BUILD.editorWebSourceCommit,
    builtAt: EDITOR_WEB_BUILD.editorWebBuiltAt,
    assetId: servedAssetId(),
  };
}

/**
 * How the three revision claims relate — the answer QA actually needs (#490).
 *
 * There are three of them and they fail in different directions, which is why
 * this is a derivation and not a single equality:
 *
 *   `web`      the commit baked into the executing bundle. Evidence.
 *   `service`  the commit baked into the running service image. Evidence.
 *   `declared` `/config.json`'s `release.commit` — the deployment's CLAIM, from
 *              a ConfigMap that stays correct while the pods run older images.
 *
 * `mismatch` therefore covers the two failures that used to be invisible: half
 * a rollout (web and service disagree) and a stale environment in front of a
 * current declaration (either image disagrees with `declared`).
 *
 * `unverifiable` is not `agrees`. A locally-built bundle carries `unknown` and a
 * pre-#490 image carries nothing, and reading either as agreement would be the
 * silent green this whole change exists to remove.
 */
export type RevisionAgreement = "agrees" | "mismatch" | "unverifiable";

const GIT_SHA = /^[0-9a-f]{40}$/;

export function revisionAgreement(
  web: WebBuildIdentity,
  service: DeploymentIdentity["service"] | undefined,
  declared: RuntimeConfig["release"],
): RevisionAgreement {
  const webCommit = GIT_SHA.test(web.sourceCommit) ? web.sourceCommit : null;
  const serviceCommit =
    service?.sourceCommitEvidence === "image-baked" &&
    typeof service.sourceCommit === "string" &&
    GIT_SHA.test(service.sourceCommit)
      ? service.sourceCommit
      : null;
  const declaredCommit =
    declared && GIT_SHA.test(declared.commit) ? declared.commit : null;

  // A release can deliberately reuse unchanged components. Compare each
  // image against its own recorded build revision; never relabel reused bytes.
  if (declared?.serviceCommit !== undefined) {
    if (
      (webCommit !== null && webCommit !== declaredCommit) ||
      (serviceCommit !== null && serviceCommit !== declared.serviceCommit) ||
      service?.sourceCommitAgreement === "mismatch"
    )
      return "mismatch";
    if (webCommit === null || serviceCommit === null || declaredCommit === null)
      return "unverifiable";
    return "agrees";
  }

  // Compare only what is present, but require BOTH images to be present before
  // agreement is claimed: one verified half plus one unknown half is exactly
  // the mixed rollout this is looking for, and calling it agreement would hide
  // the case the field exists to expose.
  const known = [webCommit, serviceCommit, declaredCommit].filter(
    (value): value is string => value !== null,
  );
  if (known.length > 1 && new Set(known).size > 1) return "mismatch";
  if (webCommit === null || serviceCommit === null) return "unverifiable";
  return "agrees";
}

/**
 * The service half of the deployment identity.
 *
 * `staleTime: 0` and no polling: this is read when a human opens the support
 * panel because they suspect the environment is stale, and a cached answer from
 * before the rollout they are asking about would be worse than no answer. It is
 * one small request, made only while the panel is open.
 */
export function useDeploymentIdentityQuery(
  enabled: boolean,
): UseQueryResult<DeploymentIdentity> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.deploymentIdentity,
    async queryFn() {
      return unwrap(await client.GET("/v1/support/deployment-identity"));
    },
    enabled,
    staleTime: 0,
  });
}
